import React from "react";
import styled from "styled-components";
import { motion } from "framer-motion";
import { Briefcase, Brain, Target, ArrowRight } from "lucide-react";

/* ======================================================
   PAGE WRAPPER
====================================================== */

const Page = styled.main`
  background: ${({ theme }) => theme.colors.bg};
  overflow-x: hidden;
  margin-top: -72px;
`;

/* ======================================================
   HIGHLIGHT
====================================================== */

const Highlight = styled.span`
  background: linear-gradient(135deg, #3b82f6, #06b6d4);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 900;
`;

/* ======================================================
   HERO
====================================================== */

const Hero = styled.section`
  padding: 220px 20px 140px;
  text-align: center;
  background: radial-gradient(
      900px at 50% -10%,
      rgba(59, 130, 246, 0.18),
      transparent 60%
    ),
    ${({ theme }) => theme.colors.bg};
`;

const Title = styled(motion.h1)`
  font-size: clamp(42px, 6vw, 64px);
  font-weight: 900;
  color: ${({ theme }) => theme.colors.text};
`;

const Sub = styled(motion.p)`
  max-width: 880px;
  margin: 26px auto 0;
  font-size: 20px;
  line-height: 1.9;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

/* ======================================================
   SECTION
====================================================== */

const Section = styled.section`
  padding: 180px 20px;
`;

const Box = styled(motion.div)`
  max-width: 960px;
  margin: auto;
  padding: 60px;
  border-radius: 32px;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.03),
    rgba(255, 255, 255, 0.01)
  );
  border: 1px solid rgba(255, 255, 255, 0.08);
  box-shadow: 0 40px 120px rgba(2, 6, 23, 0.7);
  text-align: center;
`;

const Text = styled.p`
  font-size: 18px;
  line-height: 1.9;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

/* ======================================================
   GRID
====================================================== */

const Grid = styled.div`
  max-width: 1100px;
  margin: 120px auto 0;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 36px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const Card = styled(motion.div)`
  padding: 40px;
  border-radius: 28px;
  background: linear-gradient(
    180deg,
    rgba(255, 255, 255, 0.035),
    rgba(255, 255, 255, 0.015)
  );
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const IconWrap = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 18px;
  display: grid;
  place-items: center;
  margin-bottom: 22px;
  background: linear-gradient(135deg, #3b82f6, #06b6d4);
  color: #fff;
`;

const CardTitle = styled.h4`
  font-size: 22px;
  font-weight: 900;
  margin-bottom: 12px;
  color: ${({ theme }) => theme.colors.text};
`;

const CardText = styled.p`
  font-size: 16px;
  line-height: 1.8;
  color: ${({ theme }) => theme.colors.textSecondary};
`;

/* ======================================================
   CTA
====================================================== */

const CTA = styled.section`
  padding: 200px 20px;
  text-align: center;
  background: radial-gradient(
      800px at 50% 0%,
      rgba(59, 130, 246, 0.18),
      transparent 60%
    ),
    ${({ theme }) => theme.colors.bg};
`;

const Button = styled(motion.a)`
  display: inline-flex;
  align-items: center;
  gap: 14px;
  margin-top: 48px;
  padding: 20px 36px;
  border-radius: 999px;
  font-size: 18px;
  font-weight: 900;
  color: #fff;
  background: linear-gradient(135deg, #3b82f6, #06b6d4);
  text-decoration: none;
`;

/* ======================================================
   ANIMATION
====================================================== */

const fadeUp = {
  hidden: { opacity: 0, y: 60 },
  show: { opacity: 1, y: 0, transition: { duration: 0.7 } },
};

/* ======================================================
   PAGE
====================================================== */

export default function JobPrepPage() {
  return (
    <Page>
      {/* HERO */}
      <Hero>
        <Title variants={fadeUp} initial="hidden" animate="show">
          Job Preparation,
          <br />
          <Highlight>without panic</Highlight>
        </Title>

        <Sub variants={fadeUp} initial="hidden" animate="show">
          On skilll.dev, job prep is not a separate phase.
          <br />
          It’s the <Highlight>natural outcome</Highlight> of following the right
          roadmap.
        </Sub>
      </Hero>

      {/* TRUTH */}
      <Section>
        <Box variants={fadeUp} initial="hidden" whileInView="show">
          <Text>
            Job preparation doesn’t start before interviews.
            <br />
            It starts when you stop learning{" "}
            <Highlight>randomly</Highlight>.
            <br />
            <br />
            That’s why skilll.dev builds roadmaps
            <br />
            with one clear goal:
            <br />
            <Highlight>making you job-ready</Highlight>.
          </Text>
        </Box>
      </Section>

      {/* HOW IT WORKS */}
      <Section>
        <Title
          variants={fadeUp}
          initial="hidden"
          whileInView="show"
          style={{ textAlign: "center" }}
        >
          How job prep happens here
        </Title>

        <Grid>
          <Card variants={fadeUp}>
            <IconWrap>
              <Brain size={26} />
            </IconWrap>
            <CardTitle>Skills → Questions</CardTitle>
            <CardText>
              You don’t memorize answers.
              <br />
              You build skills that naturally handle interview questions.
            </CardText>
          </Card>

          <Card variants={fadeUp}>
            <IconWrap>
              <Target size={26} />
            </IconWrap>
            <CardTitle>Projects → Resume</CardTitle>
            <CardText>
              Every project you build becomes something you can confidently
              explain in interviews.
            </CardText>
          </Card>

          <Card variants={fadeUp}>
            <IconWrap>
              <Briefcase size={26} />
            </IconWrap>
            <CardTitle>Roadmap → Readiness</CardTitle>
            <CardText>
              By the time you finish your roadmap,
              you’re not hoping for a job —
              you’re ready for one.
            </CardText>
          </Card>
        </Grid>
      </Section>

      {/* CTA */}
      <CTA>
        <Title variants={fadeUp} initial="hidden" whileInView="show">
          Want job prep that actually fits
          <br />
          <Highlight>your background</Highlight>?
        </Title>

        <Sub variants={fadeUp} initial="hidden" whileInView="show">
          Everything starts from your personalized roadmap.
        </Sub>

        <Button
          href="/my-roadmap"
          whileHover={{ scale: 1.06 }}
          whileTap={{ scale: 0.96 }}
        >
          Go to My Roadmap <ArrowRight size={18} />
        </Button>
      </CTA>
    </Page>
  );
}
